import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { Person, Unit } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { fmtMonthYear } from '@/lib/format'
import { similarNames } from '@/lib/similarNames'
import { useDebounced } from '@/lib/useDebounced'
import { SUBTITLES } from '@/shell/sectionCopy'
import {
  Avatar,
  Button,
  Card,
  EmptyRow,
  IconButton,
  PageHeader,
  Pill,
  Row,
  SearchInput,
  SectionTitle,
  Segmented,
  Spinner,
  useToast,
} from '@/ui'
import { AttachModal, type AttachScope } from './AttachModal'
import { MIN_QUERY, peopleQuery, unitsQuery, useAttachResident, useDetachResident } from './api'
import { ReviewModal } from './ReviewModal'
import { S } from './strings'
import styles from './ResidentsView.module.css'

type Step = 'review' | 'unit'

export function ResidentsView() {
  const { condoId, membership } = useAccess()
  const toast = useToast()
  const [q, setQ] = useState('')
  const [target, setTarget] = useState<Person | null>(null)
  const [step, setStep] = useState<Step>('review')
  const [tower, setTower] = useState<string | null>(null)

  const term = q.trim()
  const debounced = useDebounced(term, 250)
  const searching = term.length >= MIN_QUERY
  const people = useQuery(peopleQuery(condoId, debounced))
  const units = useQuery(unitsQuery(condoId))
  const attach = useAttachResident(condoId)
  const detach = useDetachResident(condoId)

  const towers = [...new Set((units.data ?? []).map((u) => u.tower))]
  const activeTower = tower && towers.includes(tower) ? tower : towers[0]
  const towerUnits = (units.data ?? []).filter((u) => u.tower === activeTower)
  const hits = people.data ?? []
  const settled = debounced === term && !people.isFetching

  function onAttach(unit: Unit, scope: AttachScope) {
    if (!target) return
    attach.mutate(
      { unitId: unit.id, person: target, scope },
      {
        onSuccess: () => {
          toast(S.attached(`${target.first_name} ${target.last_name}`, unit.number, unit.tower))
          setTarget(null)
          setQ('')
        },
      },
    )
  }

  function onDetach(unit: Unit, userId: number, name: string) {
    detach.mutate(
      { unitId: unit.id, userId },
      { onSuccess: () => toast(S.detached(name, unit.number)) },
    )
  }

  return (
    <>
      <PageHeader
        title="Moradores e unidades"
        subtitle={SUBTITLES.residents({ condoName: membership.condominium_name, waiting: 0 })}
      />
      <div className={styles.grid}>
        <section aria-label={S.searchTitle}>
          <SectionTitle>{S.searchTitle}</SectionTitle>
          <SearchInput value={q} onChange={setQ} placeholder={S.searchPlaceholder} />
          <div className={styles.results}>
            {!searching && (
              <Card pad={0}>
                <EmptyRow icon="search" title={S.emptyTitle} description={S.emptyDescription} />
              </Card>
            )}
            {searching && !settled && (
              <div className={styles.loading}>
                <Spinner />
              </div>
            )}
            {searching && settled && hits.length === 0 && (
              <Card pad={20} className={styles.noHit}>
                <div className={styles.noHitText}>{S.noHit(term)}</div>
                <div>
                  {/* Deviation: no invite endpoint yet; the button only announces it. */}
                  <Button
                    variant="secondary"
                    icon="mail"
                    onClick={() => toast(S.inviteSoon, { icon: 'mail' })}
                  >
                    {S.invite}
                  </Button>
                </div>
              </Card>
            )}
            {searching && settled && hits.length > 0 && (
              <Card pad={0}>
                {hits.map((p) => (
                  <HitRow
                    key={p.id}
                    person={p}
                    similar={similarNames(p, hits).length > 0}
                    onReview={() => {
                      setTarget(p)
                      setStep('review')
                    }}
                  />
                ))}
              </Card>
            )}
          </div>
        </section>

        <section aria-label={S.unitsTitle}>
          <SectionTitle
            action={
              towers.length > 1 && activeTower ? (
                <Segmented
                  aria-label="Torre"
                  value={activeTower}
                  onChange={setTower}
                  items={towers.map((t) => ({ id: t, label: t }))}
                />
              ) : undefined
            }
          >
            {S.unitsTitle}
          </SectionTitle>
          {units.isPending ? (
            <div className={styles.loading}>
              <Spinner />
            </div>
          ) : (
            <Card pad={0}>
              {towerUnits.map((u) => (
                <Row key={u.id} className={styles.unitRow}>
                  <div className={styles.unitNumber}>{u.number}</div>
                  <div className={styles.residents}>
                    {u.residents.length ? (
                      u.residents.map((r) => (
                        <div key={r.user_id} className={styles.resident}>
                          <span className={styles.residentName}>{r.name}</span>
                          <IconButton
                            icon="link_off"
                            aria-label={`${S.detach}: ${r.name}`}
                            onClick={() => onDetach(u, r.user_id, r.name)}
                          />
                        </div>
                      ))
                    ) : (
                      <span className={styles.emptyUnit}>{S.emptyUnit}</span>
                    )}
                  </div>
                </Row>
              ))}
            </Card>
          )}
        </section>
      </div>

      {target && step === 'review' && (
        <ReviewModal
          condoId={condoId}
          person={target}
          lookAlikes={similarNames(target, hits)}
          onCancel={() => setTarget(null)}
          onContinue={() => setStep('unit')}
        />
      )}
      {target && step === 'unit' && (
        <AttachModal
          person={target}
          units={units.data ?? []}
          pending={attach.isPending}
          error={attach.isError}
          onClose={() => setTarget(null)}
          onBack={() => setStep('review')}
          onAttach={onAttach}
        />
      )}
    </>
  )
}

function HitRow({
  person,
  similar,
  onReview,
}: {
  person: Person
  similar: boolean
  onReview: () => void
}) {
  const name = `${person.first_name} ${person.last_name}`
  return (
    <Row>
      <Avatar name={name} size={40} />
      <div className={styles.hitText}>
        <div className={styles.hitName}>
          <span>{name}</span>
          {similar && <Pill tone="pending">{S.similar}</Pill>}
        </div>
        <div className={styles.hitContact}>
          {person.email} · {person.phone}
        </div>
        <div className={styles.hitMeta}>
          CPF {person.tax_id_masked} · entrou em {fmtMonthYear(person.created_at)} ·{' '}
          {person.units.length ? person.units.join(', ') : S.noUnitHere}
        </div>
      </div>
      <Button
        size="s"
        variant={person.units.length ? 'secondary' : 'primary'}
        aria-label={`${S.review} ${name}`}
        onClick={onReview}
      >
        {S.review}
      </Button>
    </Row>
  )
}
